import { useEffect } from "react";

import { getNotificationSettings, upsertNotificationSettings } from "@/api/notificationSettings";
import { getOnboardingStatus } from "@/api/onboarding";
import { hasStudiedToday } from "@/api/stats";
import { AuthStatus, DEFAULT_NOTIFICATION_SETTINGS } from "@/lib/constant";
import { isNativeApp } from "@/lib/native";
import { scheduleStreakReminder, syncNotificationsToNative } from "@/lib/notificationBridge";
import { useAuthStore } from "@/store/authStore";

const initializedUserIds = new Set<string>();

export function useNotificationSettingsInitializer() {
  const userId = useAuthStore(state => state.user?.id);
  const authStatus = useAuthStore(state => state.status);

  useEffect(() => {
    if (!userId || authStatus !== AuthStatus.AUTHENTICATED || initializedUserIds.has(userId)) {
      return;
    }

    initializedUserIds.add(userId);

    let cancelled = false;

    const initialize = async () => {
      try {
        const [dbSettings, onboardingStatus] = await Promise.all([
          getNotificationSettings(userId),
          getOnboardingStatus(userId),
        ]);
        const mergedSettings = { ...DEFAULT_NOTIFICATION_SETTINGS, ...dbSettings };

        if (!dbSettings) {
          await upsertNotificationSettings(userId, DEFAULT_NOTIFICATION_SETTINGS);
        }

        // 신규 사용자는 온보딩 사전 안내 전에 OS 권한 팝업이 뜨지 않게 한다.
        if (!cancelled && onboardingStatus.isCompleted) {
          await syncNotificationsToNative(mergedSettings);
        }
      } catch {
        initializedUserIds.delete(userId);

        // 초기화 실패는 화면 동작을 막지 않음 (설정 화면에서 재시도 가능)
      }
    };

    void initialize();

    return () => {
      cancelled = true;
    };
  }, [authStatus, userId]);

  // 앱 포그라운드 복귀 시 스트릭 알림 예약 (설정 켜져 있을 때만)
  useEffect(() => {
    if (!isNativeApp || !window.SystemBridge) {
      return;
    }

    const prevHandler = window.SystemBridge.onForegrounded;

    window.SystemBridge.onForegrounded = () => {
      prevHandler?.();

      if (!userId) {
        return;
      }

      Promise.all([getNotificationSettings(userId), hasStudiedToday(userId)])
        .then(([settings, studied]) => {
          const merged = { ...DEFAULT_NOTIFICATION_SETTINGS, ...settings };

          if (merged.enabled && merged.streak_reminder && !studied) {
            scheduleStreakReminder();
          }
        })
        .catch(() => {
          // 조회 실패 시 무시 — 알림이 안 가는 것이 과도하게 가는 것보다 나음
        });
    };

    return () => {
      if (window.SystemBridge) {
        window.SystemBridge.onForegrounded = prevHandler;
      }
    };
  }, [userId]);
}
